// ไฟล์: frontend/src/pages/admin/AdminSubjectsPage.js
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "./AdminStyles.css";
import AdminSidebar from "../../components/layout/AdminSidebar";


const AdminSubjectsPage = ({ user }) => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState("add"); // 'add' หรือ 'edit'
  const [currentSubject, setCurrentSubject] = useState({
    id: "",
    name: "",
    description: "",
    icon: "book",
  });

  // ใช้ useRef สำหรับเก็บอ้างอิงถึงตารางและ DataTable instance
  const tableRef = useRef(null);
  const dataTableRef = useRef(null);

  // รายการไอคอนให้เลือก
  const iconOptions = [
    "book",
    "calculator",
    "flask",
    "language",
    "globe",
    "paint-brush",
    "music",
    "heartbeat",
    "dumbbell",
    "utensils",
    "code",
    "chart-line",
  ];

  // ดึงข้อมูลวิชาทั้งหมด
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await axios.get("/subjects");
        setSubjects(response.data.subjects);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching subjects:", error);
        setLoading(false);
      }
    };

    fetchSubjects();
  }, []);

  // เริ่มต้น DataTable หลังจากข้อมูลโหลดเสร็จ
  useEffect(() => {
    // ฟังก์ชันสำหรับเริ่มต้น DataTable
    const initializeDataTable = () => {
      // ทำลาย DataTable เก่าก่อนถ้ามี
      if (dataTableRef.current) {
        dataTableRef.current.destroy();
        dataTableRef.current = null;
      }

      // ตรวจสอบว่ามี tableRef และ jQuery DataTable พร้อมใช้งาน
      if (tableRef.current && window.$ && window.$.fn.DataTable) {
        // รอให้ DOM อัปเดตก่อนสร้าง DataTable
        setTimeout(() => {
          if (tableRef.current) {
            dataTableRef.current = window.$(tableRef.current).DataTable({
              language: {
                processing: "กำลังประมวลผล...",
                search: "ค้นหา:",
                lengthMenu: "แสดง _MENU_ รายการ",
                info: "แสดง _START_ ถึง _END_ จาก _TOTAL_ รายการ",
                infoEmpty: "แสดง 0 ถึง 0 จาก 0 รายการ",
                infoFiltered: "(กรองจากทั้งหมด _MAX_ รายการ)",
                loadingRecords: "กำลังโหลดข้อมูล...",
                zeroRecords: "ไม่พบข้อมูล",
                emptyTable: "ไม่มีข้อมูลในตาราง",
                paginate: {
                  first: "หน้าแรก",
                  previous: "ก่อนหน้า",
                  next: "ถัดไป",
                  last: "หน้าสุดท้าย",
                },
                aria: {
                  sortAscending: ": เรียงจากน้อยไปมาก",
                  sortDescending: ": เรียงจากมากไปน้อย",
                },
              },
              responsive: true,
            });
          }
        }, 100);
      }
    };

    // เริ่มต้น DataTable เมื่อข้อมูลพร้อม
    if (!loading) {
      initializeDataTable();
    }

    // Cleanup function
    return () => {
      if (dataTableRef.current) {
        dataTableRef.current.destroy();
        dataTableRef.current = null;
      }
    };
  }, [loading, subjects]); // เพิ่ม subjects เป็น dependency เพื่อให้ DataTable อัปเดตเมื่อข้อมูลเปลี่ยน

  // เปิด Modal สำหรับเพิ่มวิชาใหม่
  const handleAddNew = () => {
    setModalMode("add");
    setCurrentSubject({
      id: "",
      name: "",
      description: "",
      icon: "book",
    });

    // เปิด Modal ด้วย jQuery
    if (window.$) {
      window.$("#subjectModal").modal("show");
    }
  };

  // เปิด Modal สำหรับแก้ไขวิชา
  const handleEdit = (subject) => {
    setModalMode("edit");
    setCurrentSubject({
      id: subject.id,
      name: subject.name,
      description: subject.description || "",
      icon: subject.icon || "book",
    });

    // เปิด Modal ด้วย jQuery
    if (window.$) {
      window.$("#subjectModal").modal("show");
    }
  };

  // บันทึกข้อมูลวิชา
  const handleSave = async (e) => {
    e.preventDefault();

    try {
      let response;

      if (modalMode === "add") {
        // เพิ่มวิชาใหม่
        response = await axios.post("/subjects", currentSubject);

        Swal.fire({
          icon: "success",
          title: "เพิ่มวิชาสำเร็จ",
          text: "เพิ่มวิชาใหม่เรียบร้อยแล้ว",
          confirmButtonText: "ตกลง",
        });

        // อัพเดทรายการวิชา
        setSubjects([...subjects, response.data.subject]);
      } else {
        // แก้ไขวิชา
        response = await axios.put("/subjects", currentSubject);

        Swal.fire({
          icon: "success",
          title: "แก้ไขวิชาสำเร็จ",
          text: "แก้ไขข้อมูลวิชาเรียบร้อยแล้ว",
          confirmButtonText: "ตกลง",
        });

        // อัพเดทรายการวิชา
        const updatedSubjects = subjects.map((subject) =>
          subject.id === currentSubject.id
            ? { ...subject, ...currentSubject }
            : subject
        );
        setSubjects(updatedSubjects);
      }

      // ปิด Modal
      if (window.$) {
        window.$("#subjectModal").modal("hide");
      }
    } catch (error) {
      console.error("Error saving subject:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถบันทึกข้อมูลวิชาได้",
        confirmButtonText: "ตกลง",
      });
    }
  };

  // ลบวิชา
  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        icon: "warning",
        title: "ยืนยันการลบ",
        text: "คุณต้องการลบวิชานี้ใช่หรือไม่?",
        showCancelButton: true,
        confirmButtonText: "ลบ",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
      });

      if (result.isConfirmed) {
        await axios.delete(`/subjects/${id}`);

        Swal.fire({
          icon: "success",
          title: "ลบสำเร็จ",
          text: "ลบวิชาเรียบร้อยแล้ว",
          confirmButtonText: "ตกลง",
        });

        // อัพเดทรายการวิชา
        const updatedSubjects = subjects.filter((subject) => subject.id !== id);
        setSubjects(updatedSubjects);
      }
    } catch (error) {
      console.error("Error deleting subject:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถลบวิชาได้",
        confirmButtonText: "ตกลง",
      });
    }
  };

  // อัพเดทค่าใน Form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentSubject({
      ...currentSubject,
      [name]: value,
    });
  };

  // แสดงสถานะการโหลด
  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">กำลังโหลด...</span>
        </div>
        {/* <p>กำลังโหลดข้อมูล...</p> */}
      </div>
    );
  }

  return (
        <div className="admin-page">
      <AdminSidebar user={user} />
      <div className="admin-header">
        <h1 className="admin-title">
          <i className="fas fa-book"></i> จัดการวิชา
        </h1>
        <div className="admin-breadcrumb">
          <Link to="/admin/dashboard">แดชบอร์ด</Link> /<span>จัดการวิชา</span>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h2>รายการวิชา</h2>
          <button className="btn btn-primary" onClick={handleAddNew}>
            <i className="fas fa-plus"></i> เพิ่มวิชาใหม่
          </button>
        </div>
        <div className="admin-card-body">
          <div className="table-responsive">
            <table
              className="table table-striped"
              ref={tableRef}
              key="subjects-table"
            >
              <thead>
                <tr>
                  <th>ID</th>
                  <th>ไอคอน</th>
                  <th>ชื่อวิชา</th>
                  <th>คำอธิบาย</th>
                  <th>จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {subjects.map((subject) => (
                  <tr key={subject.id}>
                    <td>{subject.id}</td>
                    <td>
                      <i className={`fas fa-${subject.icon || "book"}`}></i>
                    </td>
                    <td>{subject.name}</td>
                    <td>{subject.description}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => handleEdit(subject)}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(subject.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal สำหรับเพิ่ม/แก้ไขวิชา */}
      <div
        className="modal fade"
        id="subjectModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {modalMode === "add" ? "เพิ่มวิชาใหม่" : "แก้ไขวิชา"}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    ชื่อวิชา
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    name="name"
                    value={currentSubject.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="description" className="form-label">
                    คำอธิบาย
                  </label>
                  <textarea
                    className="form-control"
                    id="description"
                    name="description"
                    value={currentSubject.description}
                    onChange={handleInputChange}
                    rows="3"
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="icon" className="form-label">
                    ไอคอน
                  </label>
                  <select
                    className="form-select"
                    id="icon"
                    name="icon"
                    value={currentSubject.icon}
                    onChange={handleInputChange}
                  >
                    {iconOptions.map((icon) => (
                      <option key={icon} value={icon}>
                        {icon}
                      </option>
                    ))}
                  </select>
                  <div className="mt-2">
                    <span>ตัวอย่างไอคอน: </span>
                    <i
                      className={`fas fa-${currentSubject.icon} fa-2x ms-2`}
                    ></i>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  ยกเลิก
                </button>
                <button type="submit" className="btn btn-primary">
                  บันทึก
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSubjectsPage;
